import { useMutation } from '@tanstack/react-query';
import { isNotSignedIn, signOut } from '../api';
import { clearDiagnostics } from '../feedback/diagnostics';
import { clearRecent } from '../search/recent';
import { useToast } from '../ui';
import { leaveTo } from './leave';

/** Where sign-out lands when the session had already ended. */
export const SIGNED_OUT_PATH = '/sign-in?signedOut=1';

/**
 * Real sign-out: `POST /auth/sign-out` (with the CSRF header), then a full-page navigation to
 * the address the server returns — Microsoft Entra ID's sign-out page, or
 * `/sign-in?signedOut=1` when the IdP can't be reached.
 */
export function useSignOut(): { signOut: () => void; pending: boolean } {
  const toast = useToast();
  const mutation = useMutation({
    mutationFn: signOut,
    meta: { handlesNotSignedIn: true },
    onSuccess: ({ redirectTo }) => {
      // Nothing of this person's Recent stays behind on a shared machine (SHELL1, APP-085).
      clearRecent();
      clearDiagnostics();
      leaveTo(redirectTo);
    },
    onError: (error) => {
      // The session had already ended: that's signed out too.
      if (isNotSignedIn(error)) {
        clearRecent();
        clearDiagnostics();
        leaveTo(SIGNED_OUT_PATH);
        return;
      }
      toast.show({
        tone: 'problem',
        title: 'You’re still signed in',
        body: 'We couldn’t reach MyRewards to sign you out. Try again in a moment.',
      });
    },
  });
  return {
    signOut: () => {
      if (!mutation.isPending) mutation.mutate();
    },
    pending: mutation.isPending,
  };
}
